import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Zap, Shield, TrendingUp } from 'lucide-react';

export function NardiumHero() {
  return (
    <div className="relative flex items-center justify-center min-h-[600px] overflow-hidden rounded-2xl">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-800 dark:from-violet-950 dark:via-purple-950 dark:to-indigo-950">
        {/* Animated overlay gradients */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-tr from-cyan-500/30 via-transparent to-pink-500/30"
          animate={{
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Animated particles effect */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0, 1, 0],
                scale: [0, 1.5, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-4xl mx-4 text-center">
        {/* Floating icons */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-10 left-10"
            animate={{
              y: [0, -20, 0],
              rotate: [0, 10, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
              <Zap className="w-8 h-8 text-yellow-300" />
            </div>
          </motion.div>

          <motion.div
            className="absolute top-20 right-10"
            animate={{
              y: [0, 20, 0],
              rotate: [0, -10, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5
            }}
          >
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
              <Shield className="w-8 h-8 text-cyan-300" />
            </div>
          </motion.div>

          <motion.div
            className="absolute bottom-20 left-20"
            animate={{
              y: [0, -15, 0],
              rotate: [0, 5, 0],
            }}
            transition={{
              duration: 4.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
          >
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-green-300" />
            </div>
          </motion.div>
        </div>

        {/* Main content - SIN recuadro/card container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="px-12 py-12"
        >
          {/* Logo/Badge */}
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 dark:bg-white/10 border border-white/30 mb-6"
            animate={{
              boxShadow: [
                '0 0 20px rgba(139, 92, 246, 0.3)',
                '0 0 40px rgba(139, 92, 246, 0.6)',
                '0 0 20px rgba(139, 92, 246, 0.3)',
              ]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
            }}
          >
            <Sparkles className="w-5 h-5 text-yellow-300" />
            <span className="text-sm font-semibold text-white">Next Generation DeFi</span>
          </motion.div>

          {/* Main title with gradient */}
          <motion.h1
            className="text-7xl font-bold mb-6 bg-gradient-to-r from-white via-purple-100 to-cyan-100 bg-clip-text text-transparent"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Nardium
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            className="text-2xl text-white/90 mb-8 leading-relaxed max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Your Gateway to Decentralized Finance
          </motion.p>

          {/* Description */}
          <motion.p
            className="text-lg text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            Connect your wallet to start managing your crypto assets, swap tokens,
            earn rewards through staking, and unlock exclusive features in the most
            advanced DeFi platform.
          </motion.p>

          {/* Features grid */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
          >
            <div className="bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <Zap className="w-6 h-6 text-yellow-300 mb-2 mx-auto" />
              <p className="text-white/90 text-sm font-semibold">Lightning Fast</p>
              <p className="text-white/70 text-xs mt-1">Instant transactions</p>
            </div>
            
            <div className="bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <Shield className="w-6 h-6 text-cyan-300 mb-2 mx-auto" />
              <p className="text-white/90 text-sm font-semibold">Ultra Secure</p>
              <p className="text-white/70 text-xs mt-1">Bank-grade security</p>
            </div>
            
            <div className="bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <TrendingUp className="w-6 h-6 text-green-300 mb-2 mx-auto" />
              <p className="text-white/90 text-sm font-semibold">Maximum Yield</p>
              <p className="text-white/70 text-xs mt-1">Optimized rewards</p>
            </div>
          </motion.div>

          {/* CTA hint */}
          <motion.div
            className="mt-10 text-sm text-white/80 bg-white/10 dark:bg-white/5 rounded-xl p-4 border border-white/20 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <p className="flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              Bienvenido a la plataforma DeFi más avanzada
            </p>
          </motion.div>
        </motion.div>

        {/* Glow effects */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl -z-10"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>
    </div>
  );
}